import React, { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Navbar from "./Navbar";
import Footer from "./Footer";


const FooterTermsAndConditions = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Update document title
    document.title = "Terms and Conditions - EPC Spark";
  }, []);

  return (
    <>
      <Navbar />
      <div className="privacy-policy-page">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="privacy-policy-content">
                <h1 className="text-center mb-5">Terms and Conditions</h1>

                <div className="last-updated mb-4">
                  <p className="text-muted">
                    <strong>Last Updated:</strong>{" "}
                    {new Date().toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>

                <section className="mb-5">
                  <h2>End-User License Agreement (EULA) for EPC Spark</h2>
                  <p>An EULA is a legal contract between AI4Energy Ltd (the licensor) and the end-user (the licensee) that grants the user the right to use your EPC Spark application. It specifically deals with the licensing of your software.</p>
                  
                  <h3>Key Components of a EULA for EPC Spark:</h3>
                  <h4>Grant of License:</h4>
                  <ul>
                    <li>State that AI4Energy Ltd grants the user a non-exclusive, non-transferable, revocable, limited license to use the EPC Spark app for their internal business purposes related to EPC (Energy Performance Certificate) calculations and analysis.</li>
                    <li>Specify if the license is for a single user, multiple users, or enterprise-wide.</li>
                    <li>Clarify that the license is for the object code only, and no source code is provided.</li>
                  </ul>

                  <h4>Restrictions on Use:</h4>
                  <p>Prohibited Activities: Clearly list what users cannot do with the EPC Spark app. This is critical for protecting your intellectual property and preventing misuse. Examples include:</p>
                  <ul>
                      <li>Copying, modifying, adapting, translating, reverse engineering, decompiling, disassembling, or creating derivative works of the app.</li>
                      <li>Renting, leasing, lending, selling, redistributing, or sublicensing the app.</li>
                      <li>Using the app for any illegal or unauthorized purpose.</li>
                      <li>Attempting to gain unauthorized access to AI4Energy Ltd's systems or data.</li>
                      <li>Using the app to develop a competing product or service.</li>
                      <li>Removing or altering any copyright, trademark, or other proprietary notices.</li>
                  </ul>
                  <p>Commercial Use: Explicitly state whether the app can be used for commercial purposes beyond the intended use (e.g., reselling services generated by the app). For EPC Spark, its primary purpose is likely commercial for businesses, but clarify any boundaries.</p>

                  <h4>Intellectual Property Rights:</h4>
                  <ul>
                      <li>Ownership: State that AI4Energy Ltd retains all rights, title, and interest in and to the EPC Spark app, including all associated intellectual property rights (copyrights, trademarks, trade secrets, patents, etc.).</li>
                      <li>AI-Generated Content (Output): This is crucial for an AI app.</li>
                      <li>Input Data: Clarify that users retain ownership of the data they input into the EPC Spark app.</li>
                      <li>Output Data/Results: Define who owns the output generated by the EPC Spark app based on user input. This can be complex. Common approaches include:
                          <ul>
                              <li>User owns the output for their internal use.</li>
                              <li>AI4Energy Ltd retains a license to use the anonymized/aggregated output for app improvement, research, etc. (Ensure this is compliant with data protection laws like GDPR).</li>
                          </ul>
                      </li>
                      <li>Training Data: If your AI models are trained on user data (even if anonymized), address this clearly and ensure you have the necessary rights and consents.</li>
                  </ul>

                  <h4>Updates and Maintenance:</h4>
                  <ul>
                    <li>Outline AI4Energy Ltd's policy regarding updates, upgrades, and maintenance.</li>
                    <li>State whether updates are automatic, optional, and if there are associated costs for major upgrades.</li>
                    <li>Disclaimer that AI4Energy Ltd is not obligated to provide support or maintenance unless separately agreed upon.</li>
                  </ul>

                  <h4>Termination:</h4>
                  <ul>
                      <li>Specify the conditions under which the EULA can be terminated by either party (e.g., breach of terms by the user, discontinuation of the app by AI4Energy Ltd).</li>
                      <li>Describe the consequences of termination (e.g., user must cease using the app, uninstall it, deletion of user data).</li>
                  </ul>

                  <h4>Disclaimer of Warranties (Crucial for AI):</h4>
                  <ul>
                      <li>State that the EPC Spark app is provided "as is" and "as available," without warranties of any kind, either express or implied.</li>
                      <li>Specifically address AI limitations: Given the probabilistic nature of AI, include disclaimers regarding the accuracy, completeness, reliability, or fitness for a particular purpose of the AI-generated outputs. Emphasize that the app's output is for informational purposes and should not be relied upon as sole professional advice. Users should verify results.</li>
                      <li>Disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement.</li>
                  </ul>

                  <h4>Limitation of Liability (Crucial for AI):</h4>
                  <ul>
                      <li>Limit AI4Energy Ltd's liability for any damages arising from the use or inability to use the EPC Spark app, to the maximum extent permitted by law.</li>
                      <li>Exclude liability for indirect, incidental, consequential, special, punitive, or exemplary damages (e.g., loss of profits, data, business interruption).</li>
                      <li>Consider a cap on liability, e.g., limited to the amount paid by the user for the app in the past [e.g., six (6) or twelve (12) months].</li>
                  </ul>
                  
                  <h4>Governing Law and Dispute Resolution:</h4>
                  <ul>
                    <li>Specify the governing law (e.g., the laws of England and Wales).</li>
                    <li>Outline the method for dispute resolution (e.g., negotiation, mediation, arbitration, or exclusive jurisdiction of UK courts).</li>
                  </ul>

                  <h4>Acceptance:</h4>
                  <ul>
                    <li>Describe how the user accepts the EULA (e.g., by clicking "I Agree," installing, or using the app).</li>
                  </ul>
                  
                  <h4>Contact Information:</h4>
                  <ul>
                    <li>Provide contact details for AI4Energy Ltd for any inquiries regarding the EULA.</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2>Terms and Conditions (T&Cs) for EPC Spark</h2>
                  <p>The Terms and Conditions (also known as Terms of Service or Terms of Use) is a broader agreement that governs the general use of your EPC Spark app, including user conduct, account creation, payment terms (if applicable), and other general provisions beyond just the software license.</p>
                  
                  <h3>Key Components of T&Cs for EPC Spark:</h3>
                  <h4>Introduction and Acceptance:</h4>
                  <ul>
                    <li>Welcome users and state that by accessing or using the EPC Spark app, they agree to be bound by these T&Cs.</li>
                    <li>Clearly identify the parties: AI4Energy Ltd and the user.</li>
                  </ul>

                  <h4>User Accounts:</h4>
                  <ul>
                    <li>Registration: Requirements for creating an account (e.g., age, accurate information).</li>
                    <li>Account Security: User responsibility for maintaining password confidentiality and all activities under their account.</li>
                    <li>Account Termination/Suspension: AI4Energy Ltd's right to suspend or terminate accounts for violations of T&Cs, illegal activity, or causing harm.</li>
                  </ul>

                  <h4>App Description and Services:</h4>
                  <ul>
                      <li>A brief overview of what the EPC Spark app does (e.g., "AI-powered tool for generating and analyzing Energy Performance Certificates").</li>
                      <li>Clarify the scope of services provided.</li>
                  </ul>

                  <h4>Payments and Subscriptions (if applicable):</h4>
                  <ul>
                      <li>Pricing: Clear details on fees, subscription models, and billing cycles.</li>
                      <li>Payment Methods: Accepted payment methods.</li>
                      <li>Refund Policy: Explicitly state your refund policy (or lack thereof).</li>
                      <li>Cancellations: How users can cancel subscriptions and the effect of cancellation.</li>
                      <li>Price Changes: AI4Energy Ltd's right to change prices with appropriate notice.</li>
                  </ul>

                  <h4>User Conduct and Prohibited Uses:</h4>
                  <ul>
                      <li>General guidelines for respectful and lawful use of the app.</li>
                      <li>Specific to AI: Prohibit misuse of the AI features, such as:
                          <ul>
                              <li>Attempting to manipulate the AI to generate misleading or false information.</li>
                              <li>Using the AI to create discriminatory or harmful content.</li>
                              <li>Scraping data from the app's output for unauthorized purposes.</li>
                              <li>Overloading the system with excessive requests.</li>
                          </ul>
                      </li>
                      <li>Reinforce the restrictions from the EULA (no reverse engineering, etc.).</li>
                  </ul>

                  <h4>User-Generated Content (UGC) / Input Data:</h4>
                  <ul>
                      <li>If users can upload or input data (e.g., building specifications for EPC calculations), specify:
                          <ul>
                              <li>User's responsibility for the legality, accuracy, and intellectual property rights of their input data.</li>
                              <li>AI4Energy Ltd's rights to use user input data (e.g., to process it for the app's functionality, to improve the AI model through anonymization/aggregation). Crucial for GDPR compliance.</li>
                          </ul>
                      </li>
                      <li>Disclaimer that AI4Energy Ltd is not responsible for UGC.</li>
                  </ul>

                  <h4>Third-Party Services and Links:</h4>
                  <ul>
                      <li>If the app integrates with or links to third-party services, disclaim responsibility for their content or practices.</li>
                  </ul>

                  <h4>Privacy Policy Link:</h4>
                  <ul>
                      <li>Essential: Include a prominent link to your separate Privacy Policy, which details how AI4Energy Ltd collects, uses, stores, and protects user data, particularly personal data. This is legally required under GDPR in the UK.</li>
                  </ul>

                  <h4>Disclaimer of Warranties (broader than EULA):</h4>
                  <ul>
                      <li>Reiterate that the service is provided "as is."</li>
                      <li>Disclaim any warranties regarding the uninterrupted, error-free, or secure operation of the app.</li>
                  </ul>

                  <h4>Limitation of Liability (broader):</h4>
                  <ul>
                      <li>Broadly limit AI4Energy Ltd's liability for any damages arising from the use of the app and its services.</li>
                  </ul>

                  <h4>Indemnification:</h4>
                  <ul>
                      <li>Require users to indemnify AI4Energy Ltd against any claims, damages, or expenses arising from their breach of the T&Cs or misuse of the app.</li>
                  </ul>
                  
                  <h4>Changes to Terms and Conditions:</h4>
                  <ul>
                    <li>AI4Energy Ltd's right to modify the T&Cs with appropriate notice to users.</li>
                    <li>How users will be notified of changes (e.g., in-app notification, email).</li>
                  </ul>

                  <h4>Governing Law and Dispute Resolution:</h4>
                  <ul>
                      <li>Same as in the EULA (e.g., laws of England and Wales).</li>
                  </ul>

                  <h4>Severability:</h4>
                  <ul>
                      <li>If any part of the T&Cs is found to be unenforceable, the remaining parts remain in effect.</li>
                  </ul>

                  <h4>Entire Agreement:</h4>
                  <ul>
                      <li>States that the T&Cs, along with the Privacy Policy and EULA, constitute the entire agreement between the user and AI4Energy Ltd.</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2>UK Legal Considerations for AI Apps</h2>
                  <p>When drafting these documents for a UK-based company (AI4Energy Ltd) and an AI app, pay particular attention to:</p>
                  <ul>
                      <li><strong>General Data Protection Regulation (GDPR) and the UK GDPR:</strong> Your Privacy Policy is critical for GDPR compliance, detailing lawful basis for processing, data subject rights, data retention, international data transfers (if applicable), and security measures. Explicitly address how user input data is used for AI model training, especially if it contains personal data, and ensure appropriate consents or other lawful bases. Anonymization and aggregation are key strategies to consider.</li>
                      <li><strong>Consumer Rights Act 2015:</strong> If your app is aimed at individual consumers, certain consumer rights apply, including requirements for goods (digital content) to be of satisfactory quality, fit for purpose, and as described. While EPC Spark sounds B2B, ensure you consider this if there's any B2C element.</li>
                      <li><strong>Copyright, Designs and Patents Act 1988:</strong> Protects your software's code and creative elements. Your EULA's intellectual property clauses are crucial here. The ownership of AI-generated content is an evolving area of law; clarity in your documents is paramount.</li>
                      <li><strong>Intellectual Property Office (IPO) guidance on AI and IP:</strong> Keep an eye on evolving guidance from the UK IPO regarding copyright ownership for AI-generated works and the use of copyrighted material for AI training.</li>
                      <li><strong>Emerging AI Regulation in the UK:</strong> The UK is taking a sector-specific, principles-based approach to AI regulation, rather than a single overarching AI Act like the EU. However, existing laws (data protection, consumer protection, competition law) apply to AI. Contractual considerations around liability, ethical use, and transparency of AI outputs are becoming increasingly important.</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2>Recommendation</h2>
                  <p>Use online legal generators (like TermsFeed, Termly, or Rocket Lawyer UK) as a starting point, but do not simply copy and paste. These tools provide templates, but an AI app like EPC Spark, especially one dealing with potentially critical data for energy performance, has specific nuances that require tailored legal review.</p>
                  <p>Your next step should be to engage a solicitor in the UK who specializes in intellectual property, software licensing, and data protection. They will ensure your EULA and T&Cs are legally sound, enforceable, and adequately protect AI4Energy Ltd while complying with all relevant UK laws.</p>
                </section>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </>
  );
};

export default FooterTermsAndConditions; 

